import { useCallback, useEffect, useState } from "react";
import { CalendarBlank, Fingerprint, Users, UserMinus, Alarm, ForkKnife, CheckCircle, Timer } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, authHeaders, BASE_URL } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";

const fmtTime = (iso) =>
    iso ? new Date(iso).toLocaleTimeString("es-US", { hour: "numeric", minute: "2-digit" }) : "—";
const fmtHM = (min) => (min == null ? "—" : `${Math.floor(min / 60)}h ${min % 60}m`);
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const photoSrc = (u) => (u?.startsWith("/") ? `${BASE_URL}${u}` : u);

export default function Attendance() {
    const [date, setDate] = useState(todayStr());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const r = await api.get("/admin/attendance", { params: { date: d }, headers: authHeaders() });
            setData(r.data);
        } catch {
            toast.error("No se pudo cargar la asistencia");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(date); }, [date, load]);
    // Live refresh every 30s when viewing today
    useEffect(() => {
        if (date !== todayStr()) return;
        const t = setInterval(() => load(date), 30000);
        return () => clearInterval(t);
    }, [date, load]);

    const s = data?.summary || {};
    const absentSet = new Set(data?.absent_ids || []);

    return (
        <div data-testid="admin-attendance">
            <PageHeader
                kicker="Recursos Humanos"
                title="Asistencia"
                action={
                    <a
                        href="/checador"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-timeclock"
                        className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:-translate-y-0.5 transition"
                    >
                        <Fingerprint size={16} weight="bold" />
                        Abrir Checador
                    </a>
                }
            />

            <div className="flex items-center gap-3 mb-5 flex-wrap">
                <div className="flex items-center gap-2 bg-white border border-border rounded-full px-4 py-1.5">
                    <CalendarBlank size={16} className="text-muted-foreground" />
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="attendance-date"
                        className="border-0 shadow-none focus-visible:ring-0 h-8 w-40 p-0"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <Stat icon={Users} label="Presentes" value={s.present ?? "—"} color="text-emerald-600" testid="stat-present" />
                <Stat icon={UserMinus} label="Ausentes" value={s.absent ?? "—"} color="text-rose-600" testid="stat-absent" />
                <Stat icon={Alarm} label="Retardos" value={s.late ?? "—"} color="text-amber-600" testid="stat-late" />
                <Stat icon={ForkKnife} label="En comida" value={s.on_lunch ?? "—"} color="text-sky-600" testid="stat-lunch" />
                <Stat icon={CheckCircle} label="Completados" value={s.done ?? "—"} color="text-primary" testid="stat-done" />
                <Stat icon={Timer} label="Hrs. extra" value={fmtHM(s.overtime_minutes ?? 0)} color="text-violet-600" testid="stat-overtime" />
            </div>

            <div className="bg-white rounded-2xl border border-border overflow-hidden">
                {loading && !data ? (
                    <div className="p-12 text-center text-muted-foreground">Cargando...</div>
                ) : !data || data.rows.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground" data-testid="attendance-empty">
                        <Fingerprint size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                        No hay empleados registrados todavía. Agrégalos en <b>Personal</b>.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="attendance-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-4 py-3">Empleado</th>
                                    <th className="px-4 py-3">Entrada</th>
                                    <th className="px-4 py-3">Comida</th>
                                    <th className="px-4 py-3">Regreso</th>
                                    <th className="px-4 py-3">Salida</th>
                                    <th className="px-4 py-3">Trabajado</th>
                                    <th className="px-4 py-3">Extra</th>
                                    <th className="px-4 py-3">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map(({ employee: e, record: r }, i) => {
                                    const isAbsent = absentSet.has(e.id);
                                    return (
                                        <tr key={e.id} className={`border-t border-border ${isAbsent ? "bg-rose-50/50" : ""}`} data-testid={`attendance-row-${i}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    {e.photo_url ? (
                                                        <img src={photoSrc(e.photo_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-black text-xs">
                                                            {(e.name || "?").slice(0, 1)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-bold leading-tight">{e.name}</p>
                                                        <p className="text-[10px] text-muted-foreground">{e.shift?.name || "Sin turno"}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="tabular-nums">{fmtTime(r?.entrada_at)}</span>
                                                {r?.is_late && (
                                                    <span className="ml-1.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5" data-testid={`late-badge-${i}`}>
                                                        +{r.delay_minutes}m
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 tabular-nums">{fmtTime(r?.comida_inicio_at)}</td>
                                            <td className="px-4 py-3 tabular-nums">{fmtTime(r?.comida_fin_at)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="tabular-nums">{fmtTime(r?.salida_at)}</span>
                                                {r?.early_leave_minutes > 0 && (
                                                    <span className="ml-1.5 text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5">
                                                        -{r.early_leave_minutes}m
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-semibold tabular-nums">{r?.worked_minutes != null ? fmtHM(r.worked_minutes) : "—"}</td>
                                            <td className="px-4 py-3 tabular-nums">
                                                {r?.overtime_minutes > 0 ? (
                                                    <span className="font-bold text-violet-700">{fmtHM(r.overtime_minutes)}</span>
                                                ) : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge record={r} isAbsent={isAbsent} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ record: r, isAbsent }) {
    let cls = "bg-muted text-muted-foreground border-border", label = "Sin registro";
    if (isAbsent) { cls = "bg-rose-50 text-rose-700 border-rose-200"; label = "Falta"; }
    if (r?.entrada_at) { cls = "bg-emerald-50 text-emerald-700 border-emerald-200"; label = "Presente"; }
    if (r?.comida_inicio_at && !r?.comida_fin_at && !r?.salida_at) { cls = "bg-sky-50 text-sky-700 border-sky-200"; label = "En comida"; }
    if (r?.salida_at) { cls = "bg-muted text-foreground border-border"; label = "Jornada cerrada"; }
    return (
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border ${cls}`}>
            {label}
        </span>
    );
}

function Stat({ icon: Icon, label, value, color, testid }) {
    return (
        <div className="bg-white rounded-2xl border border-border p-4" data-testid={testid}>
            <Icon size={18} weight="duotone" className={color} />
            <p className="font-display font-black text-2xl mt-1.5">{value}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        </div>
    );
}
